import { useEffect, useState } from "react";

// One editable step in the local draft. params/anchor are JSON strings (the shape
// varies by kind); the editor only edits the magic-wand's window/method inline.
export type DraftStep = { kind: string; paramsJson: string; anchorJson: string | null };

type CatalogEntry = { readonly kind: string; readonly conservesExactly: boolean };

const WAND = "MAGIC_WAND_SMOOTH";
const WINDOWS = ["PAST_12", "PAST_6", "FUTURE_6", "FUTURE_12"] as const;
const METHODS = ["EVEN", "PRORATED"] as const;

const KIND_LABEL: Record<string, string> = {
  AUTO_FREELANCER: "Auto · freelancer",
  AUTO_PROVIDER: "Auto · provider",
  PASSTHROUGH: "Passthrough · raw cash",
  MAGIC_WAND_SMOOTH: "Magic-wand · smooth a payout",
};

const param = (json: string, key: string, fallback: string) => {
  try {
    return (JSON.parse(json || "{}")[key] as string) ?? fallback;
  } catch {
    return fallback;
  }
};

// The U8 chain editor: the stakeholder's ordered transform chain, with reorder /
// remove / add and inline magic-wand window+method editing. The whole ordered set is
// saved through one mutation (Categorizations::Apply gesture); the backend validates
// conservation + anchors and rejects an invalid chain, surfaced here. The genesis ->
// terminal preview lives in the (sibling) Normalization replay section, which
// refetches after a save.
export default function TransformChainSection({
  steps: serverSteps,
  catalog,
  onSave,
}: {
  steps: readonly { readonly kind: string; readonly paramsJson: string; readonly anchorJson?: string | null }[];
  catalog: readonly CatalogEntry[];
  onSave: (steps: DraftStep[]) => Promise<void>;
}) {
  const initial = (): DraftStep[] =>
    serverSteps.map((s) => ({ kind: s.kind, paramsJson: s.paramsJson, anchorJson: s.anchorJson ?? null }));

  const [draft, setDraft] = useState<DraftStep[]>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-sync when the server chain changes (after a save / route invalidate).
  useEffect(() => setDraft(initial()), [serverSteps]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial());

  const move = (i: number, dir: -1 | 1) =>
    setDraft((d) => {
      const j = i + dir;
      if (j < 0 || j >= d.length) return d;
      const next = [...d];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const removeAt = (i: number) => setDraft((d) => d.filter((_, j) => j !== i));

  const add = (kind: string) =>
    setDraft((d) => [...d, { kind, paramsJson: "{}", anchorJson: null }]);

  const setWandParam = (i: number, key: string, value: string) =>
    setDraft((d) =>
      d.map((s, j) => {
        if (j !== i) return s;
        const params = (() => {
          try {
            return JSON.parse(s.paramsJson || "{}");
          } catch {
            return {};
          }
        })();
        return { ...s, paramsJson: JSON.stringify({ ...params, [key]: value }) };
      })
    );

  const save = () => {
    setSaving(true);
    setError(null);
    onSave(draft)
      .then(() => setSaving(false))
      .catch((e: Error) => {
        setError(e.message);
        setSaving(false);
      });
  };

  // The magic-wand is added by anchoring a transaction (the txn-table action), not
  // here — it needs a concrete payout — so the add picker offers everything else.
  const addable = catalog.map((c) => c.kind).filter((k) => k !== WAND);

  return (
    <section className="bg-white rounded border p-4 space-y-3">
      <div>
        <h2 className="font-bold text-sm">Transform chain</h2>
        <p className="text-xs text-gray-500">
          How this counterparty's cash is recognized on the normalized basis. Steps run
          top to bottom; remove the auto step for raw cash, or smooth a specific payout
          from its row in the table below.
        </p>
      </div>

      <ol className="space-y-2">
        {draft.map((step, i) => (
          <li key={i} className="flex items-center gap-2 rounded border border-gray-200 px-3 py-2">
            <span className="text-xs text-gray-400 w-5 tabular-nums">{i + 1}</span>
            <span className="text-sm font-medium">{KIND_LABEL[step.kind] ?? step.kind}</span>

            {step.kind === WAND && (
              <span className="flex items-center gap-1 text-xs text-gray-500">
                <select
                  value={param(step.paramsJson, "window", "PAST_12")}
                  onChange={(e) => setWandParam(i, "window", e.target.value)}
                  className="border rounded px-1 py-0.5"
                >
                  {WINDOWS.map((w) => (
                    <option key={w} value={w}>
                      {w.replace("_", " ").toLowerCase()}
                    </option>
                  ))}
                </select>
                <select
                  value={param(step.paramsJson, "method", "EVEN")}
                  onChange={(e) => setWandParam(i, "method", e.target.value)}
                  className="border rounded px-1 py-0.5"
                >
                  {METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m.toLowerCase()}
                    </option>
                  ))}
                </select>
                {param(step.paramsJson, "label", "") && (
                  <span className="text-gray-400">"{param(step.paramsJson, "label", "")}"</span>
                )}
              </span>
            )}

            <div className="ml-auto flex items-center gap-1 text-gray-400">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                className="px-1 hover:text-gray-700 disabled:opacity-30" title="Move up">↑</button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === draft.length - 1}
                className="px-1 hover:text-gray-700 disabled:opacity-30" title="Move down">↓</button>
              <button type="button" onClick={() => removeAt(i)}
                className="px-1 hover:text-red-600" title="Remove">✕</button>
            </div>
          </li>
        ))}
        {draft.length === 0 && (
          <li className="text-xs text-gray-400 px-1">No transforms — normalized equals raw cash.</li>
        )}
      </ol>

      <div className="flex flex-wrap items-center gap-3 text-sm">
        {addable.map((kind) => (
          <button key={kind} type="button" onClick={() => add(kind)}
            className="text-emerald-600 hover:text-emerald-800">
            + {KIND_LABEL[kind] ?? kind}
          </button>
        ))}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {dirty && (
        <div className="flex items-center gap-2 pt-1">
          <button type="button" onClick={save} disabled={saving}
            className="px-3 py-1.5 text-sm rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40">
            {saving ? "Saving…" : "Save chain"}
          </button>
          <button type="button" onClick={() => { setDraft(initial()); setError(null); }} disabled={saving}
            className="px-3 py-1.5 text-sm rounded border hover:bg-gray-50">
            Discard
          </button>
        </div>
      )}
    </section>
  );
}
